import { z } from 'zod';
import { LocaleSchema } from './specialization';

/**
 * CMS content blocks (ADR-004 / ADR-008) — owner-editable copy, keyed by
 * `(namespace, key)`, bilingual, draft → publish workflow. Frontends only
 * ever see published content; the draft body never leaves the admin API.
 */

export const ContentBlockTranslationSchema = z.object({
  locale: LocaleSchema,
  draftBody: z.string().min(1).max(20000),
  publishedBody: z.string().max(20000).nullable(),
  publishedAt: z.string().datetime().nullable(),
});
export type ContentBlockTranslation = z.infer<typeof ContentBlockTranslationSchema>;

/** Admin view — includes drafts. GET /api/v1/admin/cms/content-blocks */
export const ContentBlockSchema = z.object({
  id: z.string().cuid(),
  namespace: z.string().min(1).max(80),
  key: z.string().min(1).max(120),
  isPublished: z.boolean(),
  translations: z.array(ContentBlockTranslationSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ContentBlock = z.infer<typeof ContentBlockSchema>;

export const AdminContentBlockListQuerySchema = z.object({
  namespace: z.string().min(1).max(80).optional(),
});
export type AdminContentBlockListQuery = z.infer<typeof AdminContentBlockListQuerySchema>;

export const AdminContentBlockListResponseSchema = z.object({
  blocks: z.array(ContentBlockSchema),
});
export type AdminContentBlockListResponse = z.infer<typeof AdminContentBlockListResponseSchema>;

/** PUT /api/v1/admin/cms/content-blocks/:namespace/:key — upsert draft bodies. */
export const UpsertContentBlockRequestSchema = z.object({
  translations: z
    .array(z.object({ locale: LocaleSchema, draftBody: z.string().min(1).max(20000) }))
    .min(1),
});
export type UpsertContentBlockRequest = z.infer<typeof UpsertContentBlockRequestSchema>;

export const ContentBlockResponseSchema = z.object({ block: ContentBlockSchema });
export type ContentBlockResponse = z.infer<typeof ContentBlockResponseSchema>;

/**
 * Public read — GET /api/v1/content/:namespace. Published bodies only,
 * `en` fallback applied when a locale is missing (ADR-004). Shape is a flat
 * `key -> body` map, the form frontends actually consume.
 */
export const PublicContentResponseSchema = z.object({
  namespace: z.string(),
  locale: LocaleSchema,
  content: z.record(z.string(), z.string()),
});
export type PublicContentResponse = z.infer<typeof PublicContentResponseSchema>;
